import React, { FormEvent, useState } from 'react';
import { Element } from 'react-scroll';
import { useAsync } from 'react-use';
import { always, isEmpty } from 'ramda';
import { FormattedMessage, useIntl } from 'react-intl';
import NextLink from 'next/link';

import { ButtonContainer, ContinueReadingButton } from '@components/continueReadingButtonArtist';
import { ContentBody } from '@components/contentBody';
import { ArtistPage } from '@definitions/artists.types';
import { Block } from '@definitions/common.types';
import {
  NewsletterBlock,
  NoArtworks,
  ParagraphWithTitle,
  PARAGRAPH_WITH_TITLE_OPTION,
  ProductsBlock,
} from '@components/blocks';
import { useMonogram } from '@hooks/useMonogram';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { GalleryRepresentation } from '@components/galleryRepresentation';
import { getFullArtistName, getHasProducts } from '@utils/helpers';
import { PastArtworks } from '@components/blocks/pastArtworks';
import { NotifyForm } from '@components/notifyForm';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { useCustomer } from '@hooks/useCustomer';
import { CONVERSIONS_EVENTS, VIEW_CONTENT_TYPES } from '@definitions/conversions.types';
import { ROUTE } from '@utils/constants';
import api from '@services/nextAPI';

import {
  ArtistLink,
  ArtistsLinksContainer,
  ArtistLinkContent,
  Container,
  ProductsContainer,
  ContainerNarrow,
  ContainerInner,
} from './artistView.styles';

export const CONTENT_BODY_ELEMENT_NAME = 'artist-body';

export interface ArtistViewProps {
  artistPage: ArtistPage;
}

export const ArtistView = ({ artistPage }: ArtistViewProps) => {
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isContinuReadingClicked, setIsContinuReadingClicked] = useState(false);
  const { customer } = useCustomer();
  const { addMonogram, clearMonogram } = useMonogram();
  const { sendConversion } = useTrackConversions();
  const hasProducts = artistPage?.artistProducts ? getHasProducts(artistPage.artistProducts) : false;
  const hasPastProducts = artistPage?.pastArtistProducts ? getHasProducts(artistPage.pastArtistProducts) : false;
  const paragraphTextCount = artistPage?.biography ? artistPage?.biography[0].value.paragraph.length : 0;

  const intl = useIntl();

  const newsletterSuccessInformation = intl.formatMessage({
    id: 'artistView.newsletterSuccessInformation',
    defaultMessage: "Thank you! You've successfully subscribed to the artist newsletter.",
  });
  const newsletterTitle = intl.formatMessage({
    id: 'artistView.newsletterTitle',
    defaultMessage: 'NEWSLETTER:',
  });
  const newsletterInfoText = intl.formatMessage(
    {
      id: 'artistView.newsletterInfoText',
      defaultMessage: "To receive e-mail updates from {artistName}, please sign up to the gallery's newsletter below:",
    },
    { artistName: artistPage.title }
  );

  const handleNotifySubmit = async (e: FormEvent, formData: { email: string; firstName: string; lastName: string }) => {
    e.preventDefault();
    const { email, firstName, lastName } = formData;
    await api.post('email/subscribe', {
      email,
      firstName,
      lastName,
      tags: [`Artist page ID ${artistPage.id}`],
    });
    setIsConfirmationVisible(true);
  };

  useAsync(async () => {
    const id = new Date().getTime();

    if (artistPage.monogram?.monogramLetter?.length) {
      const { monogramLetter, monogramColor, monogramAnimationType } = artistPage.monogram;

      addMonogram({
        letter: monogramLetter,
        color: monogramColor,
        appearance: monogramAnimationType,
        id,
      });
    }

    await sendConversion({
      eventType: CONVERSIONS_EVENTS.VIEW_CONTENT,
      contentName: VIEW_CONTENT_TYPES.ARTIST_PLP,
    });

    return () => {
      clearMonogram(id);
    };
  }, []);

  const renderBiography = (biography: Block, index: number) => (
    <ParagraphWithTitle key={index} {...biography.value} isContinuReadingClicked={isContinuReadingClicked} />
  );

  const renderProducts = (artistProducts: Block, index: number) => (
    <ProductsContainer key={index}>
      <ProductsBlock artworks={artistProducts.value} hasSpacingTop={false} />
    </ProductsContainer>
  );

  const renderGalleryRepresentationBlock = renderWhenTrue(() => (
    <GalleryRepresentation galleries={artistPage.representedBy} />
  ));

  const renderArtistProducts = renderWhenTrue(always(artistPage?.artistProducts?.map(renderProducts)));

  const renderNoArtworks = renderWhenTrue(always(<NoArtworks />));

  const renderContinueReadingButton = renderWhenTrue(
    always(
      <ButtonContainer>
        <a
          onClick={() => {
            setIsContinuReadingClicked(!isContinuReadingClicked);
          }}
        >
          <ContinueReadingButton isContinuReadingClicked={isContinuReadingClicked} />
        </a>
      </ButtonContainer>
    )
  );

  const renderPastArtworks = renderWhenTrue(always(<PastArtworks blocks={artistPage.pastArtistProducts} />));

  const renderArtistsLinks = renderWhenTrue(
    always(
      <ArtistsLinksContainer>
        {/* eslint-disable-next-line @typescript-eslint/restrict-template-expressions */}
        <NextLink href={`${ROUTE.ARTISTS}/${artistPage?.previousArtist?.slug}`} passHref>
          <ArtistLink>
            <ArtistLinkContent>
              <FormattedMessage id="artistView.previous" defaultMessage="Previous:" />
            </ArtistLinkContent>
            <ArtistLinkContent>
              {artistPage.previousArtist && getFullArtistName(artistPage?.previousArtist)}
            </ArtistLinkContent>
          </ArtistLink>
        </NextLink>
        {/* eslint-disable-next-line @typescript-eslint/restrict-template-expressions */}
        <NextLink href={`${ROUTE.ARTISTS}/${artistPage?.nextArtist?.slug}`} passHref>
          <ArtistLink>
            <ArtistLinkContent>
              <FormattedMessage id="artistView.next" defaultMessage="Next:" />
            </ArtistLinkContent>
            <ArtistLinkContent>{artistPage.nextArtist && getFullArtistName(artistPage.nextArtist)}</ArtistLinkContent>
          </ArtistLink>
        </NextLink>
      </ArtistsLinksContainer>
    )
  );

  const renderReducedView = always(
    <>
      <ParagraphWithTitle
        title={artistPage.title}
        paragraph="This artist is coming soon to Platform. Sign up for our email list to be notified when their artworks become available."
        displayOption={PARAGRAPH_WITH_TITLE_OPTION.BIOGRAPHY}
        isContinuReadingClicked={isContinuReadingClicked}
      />
      <ContainerNarrow>
        <NotifyForm
          onFormSubmit={(e, { email, firstName, lastName }) => handleNotifySubmit(e, { email, firstName, lastName })}
          isConfirmationVisible={isConfirmationVisible}
          texts={{
            info: '',
            confirmation: 'You will be notified when the artist becomes available.',
            submit: 'SIGN UP',
          }}
          initialFields={{
            email: customer?.email,
            firstName: customer?.firstName,
            lastName: customer?.lastName,
          }}
        />
      </ContainerNarrow>
    </>
  );

  const renderFullView = always(
    <>
      {artistPage?.biography?.map(renderBiography)}
      {paragraphTextCount > 256 && renderContinueReadingButton(artistPage.showContinuedReading)}
      {renderArtistProducts(hasProducts)}
      {renderPastArtworks(!hasProducts)}
      {renderNoArtworks(!hasProducts && !hasPastProducts)}
      <Element name={CONTENT_BODY_ELEMENT_NAME}>
        <ContentBody body={artistPage.body}>
          {renderGalleryRepresentationBlock(!isEmpty(artistPage.representedBy))}
        </ContentBody>
      </Element>
      <ContainerInner>
        <NewsletterBlock
          infoText={newsletterInfoText}
          successInformation={newsletterSuccessInformation}
          tag={artistPage.title}
          title={newsletterTitle}
        />
      </ContainerInner>
      {renderPastArtworks(hasProducts)}
      {renderArtistsLinks(!!artistPage.previousArtist && !!artistPage.nextArtist)}
    </>
  );

  const renderView = renderWhenTrueOtherwise(renderReducedView, renderFullView);

  return <Container>{renderView(artistPage.reducedVersion)}</Container>;
};
